import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
    const { login, refresh, formatApiErrorDetail } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/";
  const workspace = searchParams.get("workspace");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      if (workspace === "events" || workspace === "freelance") {
        await api.patch("/users/me", { last_workspace: workspace });
        await refresh();
      }
      toast.success("Welcome back!");
      nav(redirectTo);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const googleLogin = async () => {
    try {
      // Logout from backend first to clear server-side cookie
      await api.post("/auth/logout");
    } catch {}
    // Clear all client-side tokens
    localStorage.removeItem("em_token");
    document.cookie = "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname;
    document.cookie = "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
    const callbackUrl = `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(redirectTo)}`;
    window.location.href = `${backendUrl}/api/auth/google/login?redirect=${encodeURIComponent(callbackUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block shell-dark text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(168,85,247,0.35), transparent 70%)" }} />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(236,76,184,0.25), transparent 70%)" }} />
        <div className="relative">
          <span className="label-eyebrow text-muted-foreground">Welcome back to</span>
          <h1 className="mt-3 font-display text-6xl tracking-tighter leading-none bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(90deg, #ec4cb8 0%, #c026d3 35%, #a855f7 70%, #7c3aed 100%)" }}>EarnMate</h1>
          <p className="mt-6 text-slate-300 max-w-md">Pick up where you left off. Your applications, gigs and earnings — all in one place.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h2 className="font-display text-3xl tracking-tight">Sign in</h2>
          <p className="mt-1 text-sm text-slate-600">No account? <Link to={redirectTo !== "/" ? `/register?redirect=${encodeURIComponent(redirectTo)}` : "/register"} className="underline">Create one</Link></p>

          <button type="button" onClick={googleLogin} className="mt-6 btn-outline w-full" data-testid="google-login-btn">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground"><div className="flex-1 h-px bg-slate-200"/>OR<div className="flex-1 h-px bg-slate-200"/></div>

          <div className="space-y-4">
            <div>
              <label className="label-eyebrow text-muted-foreground">Email</label>
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" required className="input-base mt-2" data-testid="login-email" />
            </div>
            <div>
              <label className="label-eyebrow text-muted-foreground">Password</label>
              <div className="relative mt-2">
                <input value={password} onChange={e => setPassword(e.target.value)} type={showPassword ? "text" : "password"} required className="input-base pr-10" data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Link to="/forgot-password" className="block text-xs text-slate-600 mt-2 underline">Forgot password?</Link>
            </div>
          </div>

          <button disabled={loading} className="btn-primary w-full mt-6" data-testid="login-submit">{loading ? "Signing in…" : "Sign in"}</button>
         <p className="mt-4 text-center text-xs text-muted-foreground">Don't have an account? <Link to={redirectTo !== "/" ? `/register?redirect=${encodeURIComponent(redirectTo)}` : "/register"} className="underline text-brand">Sign up</Link></p>
        </form>
      </div>
    </div>
  );
}
